import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Heart, BookOpen, Users, Flower2, ArrowRight, MapPin } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e1e47677-df71-49c4-917c-4fa0a875df63/artifacts/mtdbnete_Untitled%20design%20-%202026-01-07T124500.690.png";
const HERO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_e1e47677-df71-49c4-917c-4fa0a875df63/artifacts/tjmt8d51_1745947-hd_1920_1080_30fps.mp4";
const MONICA_IMAGE = "https://customer-assets.emergentagent.com/job_substack-connect/artifacts/84vc8aqz_cc7c3bcb-afc6-454e-aa8b-a9edbd692b62.jpg";

const services = [
  {
    title: "Advance Care Planning",
    description: "Guidance in completing important end-of-life documents including Advance Directive, Healthcare Power of Attorney, and Living Will.",
    icon: BookOpen,
    link: "/advance-care-planning",
  },
  {
    title: "End-of-Life Coaching and Care Coordination",
    description: "Compassionate emotional support and advocacy across healthcare settings, ensuring your voice and values remain central.",
    icon: Heart,
    link: "/end-of-life-coaching",
  },
  {
    title: "Legacy & Life Review",
    description: "Create meaningful keepsakes through memory books, letters, and guided life reflections for your loved ones.",
    icon: Users,
    link: "/legacy-projects",
  },
  {
    title: "Vigil & Funeral Planning",
    description: "Support in honoring final wishes with intention, including symptom comfort, environment rituals, and funeral planning.",
    icon: Flower2,
    link: "/vigil-and-funeral-planning",
  },
];

const Home = () => {
  return (
    <>
      <Helmet>
        <title>Serenity Passage | End-of-Life Doula in Columbia, MO</title>
        <meta
          name="description"
          content="Compassionate end-of-life doula services in Columbia, Missouri. Hospice support, advance care planning, and emotional guidance for families."
        />
        <meta
          name="keywords"
          content="end-of-life doula Columbia MO, death doula Columbia Missouri, hospice support Columbia MO, advance care planning Mid-Missouri, end-of-life support near me, end-of-life doula Mid-Missouri"
        />
        <link rel="canonical" href="https://www.serenitypassage.net/" />
      </Helmet>

      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>
        
        {/* White/Light Overlay for logo visibility */}
        <div className="absolute inset-0 bg-white/60"></div>
        
        {/* Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          {/* Centered Large Logo */}
          <img
            src={LOGO_URL}
            alt="Serenity Passage - End-of-Life Doula in Columbia, MO"
            className="mx-auto h-64 sm:h-80 md:h-96 lg:h-[28rem] w-auto object-contain mb-8"
            data-testid="hero-logo"
          />
          
          {/* Hero Overlay Text */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-light text-gray-800 mb-6" data-testid="hero-title">
            End-of-Life Doula in Columbia, MO
          </h1>
          <p className="text-lg sm:text-xl text-gray-700 mb-8" data-testid="hero-subtitle">
            Compassionate Support for Life's Final Journey
          </p>
          
          {/* Tagline */}
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed" data-testid="hero-tagline">
            Serenity Passage provides compassionate, non-medical end-of-life doula services in Columbia, Missouri and Mid-Missouri. Whether you are planning ahead or currently supporting a loved one nearing end-of-life, I am here to support you with dignity, comfort, and peace.
          </p>
        </div>
      </section>

      {/* About/Intro Section */}
      <section className="py-20 bg-gradient-to-b from-rose-50 to-white" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl font-serif text-gray-800 mb-6">
                About My Journey
              </h2>
              <p className="text-gray-700 leading-relaxed text-lg" data-testid="intro-paragraph">
                With more than 22 years of experience as a Registered Nurse, I've had the privilege of caring for individuals and families through every stage of life. Through this work, I've come to understand dying as not a medical failure, but as a natural and sacred part of the human experience.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 mt-6 text-rose-600 hover:text-rose-700 font-medium transition-colors"
                data-testid="learn-more-link"
              >
                Learn more about my journey
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="order-1 lg:order-2 flex justify-center">
              <img
                src={MONICA_IMAGE}
                alt="Monica Korba, RN - End-of-Life Doula in Columbia, MO"
                className="rounded-2xl shadow-xl max-w-sm w-full"
                loading="lazy"
                width="384"
                height="512"
                data-testid="monica-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="py-16 bg-white" data-testid="service-area-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <MapPin className="w-8 h-8 text-rose-600" />
            <h2 className="text-3xl sm:text-4xl font-serif text-gray-800">
              Serving Columbia, Missouri & Mid-Missouri Families
            </h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Serenity Passage provides end-of-life doula services to families in Columbia, MO and surrounding Mid-Missouri communities. As a local death doula based in Columbia, Missouri, I offer personalized hospice support, advance care planning, and compassionate guidance to help you and your loved ones navigate this sacred journey.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Whether you're seeking end-of-life support near Columbia, hospice guidance in Mid-Missouri, or advance care planning assistance, I'm here to serve you with dignity and compassion.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-b from-white to-rose-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif text-gray-800 mb-4">
              Compassionate Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Supporting you through every aspect of end-of-life planning and care
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-rose-50 rounded-2xl p-8 hover:shadow-lg transition-shadow duration-300"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-10 h-10 text-rose-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                <Link
                  to={service.link}
                  className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Let's Connect Section */}
      <section className="py-20 bg-rose-50" data-testid="connect-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-serif text-gray-800 mb-6">
            Let's Connect
          </h2>
          <p className="text-gray-700 text-lg mb-8" data-testid="connect-text">
            I would be honored to support you and your family during this time. Your first 30-minute consultation is complimentary.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-full font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
            data-testid="contact-cta"
          >
            Schedule a Consultation
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 bg-rose-50" data-testid="quote-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <blockquote className="text-center">
            <p className="text-xl sm:text-2xl text-gray-700 italic font-light leading-relaxed">
              "I believe in the strengths of traditional medicine while also embracing holistic, client-centered care that respects the whole person: mind, body, and spirit."
            </p>
            <footer className="mt-6 text-gray-600 font-medium">
              Monica Korba, RN
            </footer>
          </blockquote>
        </div>
      </section>
    </>
  );
};

export default Home;
